import { CloseIcon } from "@imi/icons/CloseIcon.js";
import { Button } from "@imi/ui/components/Button/Button.js";
import { useFieldContext } from "@imi/ui/hooks/useAppForm.js";
import { CustomPipeSeries } from "@src/core/api/customPipeSeries";
import { AddPipeSeriesDefinitions } from "@src/core/engine/pipes/pipesRepository";
import { useTranslation } from "react-i18next";
import { useCustomPipeSeriesForm } from "../hooks/useCustomPipeSeriesForm";
import styles from "./PipeFieldset.module.css";

type PipeFieldsetProps = {
  className?: string;
  definitions: AddPipeSeriesDefinitions;
  form: ReturnType<typeof useCustomPipeSeriesForm>["form"];
  index: number;
};

export function PipeFieldset({
  className,
  definitions,
  form,
  index,
}: PipeFieldsetProps) {
  const { t } = useTranslation("settings");
  const field = useFieldContext<CustomPipeSeries["pipes"]>();

  return (
    <fieldset className={className}>
      <legend>
        {t("pipeSeries.add.actions.newPipe")}
        {field.state.value.length > 1 && (
          <Button
            onClick={() => {
              field.removeValue(index);
            }}
            variant="ghost"
          >
            <CloseIcon />
            {t("pipeSeries.add.actions.deletePipe")}
          </Button>
        )}
      </legend>

      <div className={styles.PipeFields}>
        <form.AppField name={`pipes[${index}].name`}>
          {(pipeField) => (
            <pipeField.TextField
              errors={pipeField.state.meta.errors.map((error) =>
                error ? t(error.message) : undefined,
              )}
              label={t("pipeSeries.add.fields.pipes.name.label")}
              placeholder={t("pipeSeries.add.fields.pipes.name.placeholder")}
            />
          )}
        </form.AppField>

        <form.AppField name={`pipes[${index}].valveSize`}>
          {(valveSizeField) => (
            <valveSizeField.Select
              label={t("pipeSeries.add.fields.pipes.valveSize.label")}
            >
              {definitions.pipes.valveSize.options.map((valveSize) => (
                <option key={valveSize.id} value={valveSize.id}>
                  {valveSize.label}{" "}
                  {valveSize.default ? t("pipeSeries.add.defaultOption") : ""}
                </option>
              ))}
            </valveSizeField.Select>
          )}
        </form.AppField>

        <form.AppField name={`pipes[${index}].internalDiameter`}>
          {(internalDiameterField) => (
            <internalDiameterField.NumberField
              definition={definitions.pipes.internalDiameter}
              errors={internalDiameterField.state.meta.errors.map((error) =>
                error ? t(error.message) : undefined,
              )}
              label={t("pipeSeries.add.fields.pipes.internalDiameter.label")}
              placeholder={t(
                "pipeSeries.add.fields.pipes.internalDiameter.placeholder",
              )}
            />
          )}
        </form.AppField>

        <form.AppField name={`pipes[${index}].roughness`}>
          {(roughnessField) => (
            <roughnessField.NumberField
              definition={definitions.pipes.roughness}
              errors={roughnessField.state.meta.errors.map((error) =>
                error ? t(error.message) : undefined,
              )}
              label={t("pipeSeries.add.fields.pipes.roughness.label")}
              placeholder={t(
                "pipeSeries.add.fields.pipes.roughness.placeholder",
              )}
            />
          )}
        </form.AppField>

        <form.AppField name={`pipes[${index}].maxPressure`}>
          {(maxPressureField) => (
            <maxPressureField.NumberField
              definition={definitions.pipes.maxPressure}
              errors={maxPressureField.state.meta.errors.map((error) =>
                error ? t(error.message) : undefined,
              )}
              label={t("pipeSeries.add.fields.pipes.maxPressure.label")}
              placeholder={t(
                "pipeSeries.add.fields.pipes.maxPressure.placeholder",
              )}
            />
          )}
        </form.AppField>

        <form.AppField name={`pipes[${index}].maxTemperature`}>
          {(maxTemperatureField) => (
            <maxTemperatureField.NumberField
              definition={definitions.pipes.maxTemperature}
              errors={maxTemperatureField.state.meta.errors.map((error) =>
                error ? t(error.message) : undefined,
              )}
              label={t("pipeSeries.add.fields.pipes.maxTemperature.label")}
              placeholder={t(
                "pipeSeries.add.fields.pipes.maxTemperature.placeholder",
              )}
            />
          )}
        </form.AppField>
      </div>
    </fieldset>
  );
}
